import React from 'react';
import { CheckCircle2, Circle, AlertCircle } from 'lucide-react';

/* ─── Helpers ─────────────────────────────────────────────────────── */

function deriveDefaultMilestones(team, members, progress) {
  // Milestones 1-3 (assessment, valuation, financing) flip to `completed: true`
  // when the corresponding signal is satisfied — `progress` is derived
  // server-side by DealTeamProgress (app/services/deal_team_progress.rb):
  //   • assessment: TriggeringEvent(event_type: :smb_readiness_assessment) exists for the owner
  //   • valuation:  team has a shared VaultDocument with source_type='valuation_snapshot'
  //   • financing:  team has a shared VaultDocument with source_type='financing_snapshot'
  //
  // Milestones 4-6 (legal_review, letter_of_intent, close) intentionally stay
  // false in V1 — see todos/099-pending-p3-deal-team-milestone-wiring-4-6.md
  // for the deferred signal definitions.
  const memberByRole = {};
  (members || []).forEach((m) => {
    if (!memberByRole[m.role]) memberByRole[m.role] = m;
  });

  const filledRoles = new Set(Object.keys(memberByRole));
  const isComplete = (id) => !!progress?.[id];

  return [
    {
      id: 'assessment',
      label: 'Assessment Complete',
      completed: isComplete('assessment'),
      responsibleRole: 'YOU',
      roleEmpty: false,
    },
    {
      id: 'valuation',
      label: 'Valuation Snapshot',
      completed: isComplete('valuation'),
      responsibleRole: 'CBV',
      roleEmpty: !filledRoles.has('cbv'),
    },
    {
      id: 'financing',
      label: 'Financing Scenarios',
      completed: isComplete('financing'),
      responsibleRole: 'LENDER',
      roleEmpty: !filledRoles.has('lender'),
    },
    {
      id: 'deal_structure',
      label: 'Deal Structure',
      completed: isComplete('deal_structure'),
      responsibleRole: 'BROKER',
      roleEmpty: !filledRoles.has('business_broker'),
    },
    {
      id: 'legal_review',
      label: 'Legal Review',
      completed: isComplete('legal_review'),
      responsibleRole: 'LAWYER',
      roleEmpty: !filledRoles.has('lawyer'),
    },
    {
      id: 'letter_of_intent',
      label: 'Letter of Intent',
      completed: isComplete('letter_of_intent'),
      responsibleRole: 'BROKER',
      roleEmpty: !filledRoles.has('business_broker'),
    },
    {
      id: 'close',
      label: 'Close',
      completed: isComplete('close'),
      responsibleRole: 'FULL TEAM',
      roleEmpty: false,
    },
  ];
}

const ROLE_LABEL = {
  cbv: 'CBV',
  business_broker: 'BROKER',
  lawyer: 'LAWYER',
  accountant: 'ACCOUNTANT',
  lender: 'LENDER',
  cepa_coach: 'CEPA',
};

/* ─── DealProgressTracker ─────────────────────────────────────────── */

// Manual checkpoints (todos/099 / D9-B): owner-driven completion via the
// MarkMilestone mutation. The "Mark complete" affordance is rendered only
// for the team owner.
const MANUAL_CHECKPOINT_IDS = new Set(['legal_review', 'letter_of_intent', 'close']);

const DealProgressTracker = ({
  team,
  members = [],
  milestones,
  progress,
  currentUserId,
  teamOwnerUserId,
  onMarkMilestone,
}) => {
  const computedMilestones = milestones || deriveDefaultMilestones(team, members, progress);
  const totalRoles = 6;
  const filledCount = (members || []).length;
  const completedMilestones = computedMilestones.filter((m) => m.completed).length;
  const nextPendingMilestone = computedMilestones.find((m) => !m.completed);
  const nextMilestoneNeedsRole = !!nextPendingMilestone?.roleEmpty;
  const isOwner =
    currentUserId != null &&
    teamOwnerUserId != null &&
    String(currentUserId) === String(teamOwnerUserId);

  return (
    <div className="max-w-2xl">
      <h2 className="font-serif text-[24px] font-light text-[hsl(var(--foreground))] mb-6">
        Deal Progress
      </h2>

      {/* Closed-deal banner — shown once the close milestone has flipped. */}
      {team?.status === 'closed' && (
        <div className="mb-6 rounded-lg border border-[hsl(var(--succeedo-orange))]/30 bg-[hsl(var(--succeedo-orange))]/5 px-4 py-3 text-[13px] text-[hsl(var(--foreground))]">
          <span className="font-medium">Deal closed.</span> The workspace is read-only —
          start a new deal team when you&rsquo;re ready.
        </div>
      )}

      {/* Team Readiness Summary */}
      <div className="bg-[hsl(var(--muted))]/50 rounded-lg p-4 mb-8">
        <div className="text-[13px] text-[hsl(var(--foreground))]">
          <span className="font-medium">{filledCount} of {totalRoles}</span> roles filled
          <span className="mx-1">·</span>
          <span className="font-medium">{completedMilestones} milestones</span> complete
        </div>
        {nextMilestoneNeedsRole && nextPendingMilestone && (
          <div className="flex items-center gap-2 mt-2 text-[12px] text-[hsl(var(--succeedo-orange))]">
            <AlertCircle className="w-3.5 h-3.5" />
            <span>
              Next step needs a {nextPendingMilestone.responsibleRole} — add one to keep momentum
            </span>
          </div>
        )}
      </div>

      {/* Milestones */}
      <div className="space-y-0">
        {computedMilestones.map((milestone, index) => (
          <div key={milestone.id} className="flex gap-4">
            {/* Icon Column */}
            <div className="flex flex-col items-center">
              {milestone.completed ? (
                <CheckCircle2 className="w-[18px] h-[18px] text-[hsl(var(--succeedo-orange))] flex-shrink-0" />
              ) : (
                <Circle className="w-[18px] h-[18px] text-[hsl(var(--muted-foreground))]/20 flex-shrink-0" />
              )}
              {index < computedMilestones.length - 1 && (
                <div className="w-px h-8 bg-[hsl(var(--border))] ml-[9px]" />
              )}
            </div>

            {/* Content Column */}
            <div className="pb-8 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <div
                  className={`text-[14px] font-medium ${
                    milestone.completed
                      ? 'text-[hsl(var(--foreground))]'
                      : 'text-[hsl(var(--muted-foreground))]/40'
                  }`}
                >
                  {milestone.label}
                </div>
                {!milestone.completed && milestone.responsibleRole && (
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-[10px] uppercase text-[hsl(var(--muted-foreground))]/60 bg-[hsl(var(--muted))]/50 px-2 py-0.5 rounded">
                      {ROLE_LABEL[milestone.responsibleRole] || milestone.responsibleRole}
                    </span>
                    {milestone.roleEmpty && (
                      <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))]" />
                    )}
                  </div>
                )}
                {/* Manual-checkpoint affordance — owner-only. */}
                {!milestone.completed &&
                  isOwner &&
                  MANUAL_CHECKPOINT_IDS.has(milestone.id) &&
                  onMarkMilestone && (
                    <button
                      type="button"
                      onClick={() => onMarkMilestone(milestone.id)}
                      className={`ml-auto px-3 py-1 rounded-full border text-[11px] font-medium transition-colors ${
                        milestone.id === 'close'
                          ? 'border-[hsl(var(--succeedo-orange))] bg-[hsl(var(--succeedo-orange))] text-white hover:opacity-90'
                          : 'border-[hsl(var(--succeedo-orange))] text-[hsl(var(--succeedo-orange))] hover:bg-[hsl(var(--succeedo-orange))]/10'
                      }`}
                    >
                      {milestone.id === 'close' ? 'Close deal' : 'Mark complete'}
                    </button>
                  )}
              </div>
              {milestone.completed && (
                <div className="flex items-center gap-3">
                  {milestone.date && (
                    <span className="font-mono text-[11px] text-[hsl(var(--muted-foreground))]/35">
                      {milestone.date}
                    </span>
                  )}
                  {milestone.partner && (
                    <span className="text-[13px] text-[hsl(var(--muted-foreground))]">
                      {milestone.partner}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DealProgressTracker;
